import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import BusyOverlay from "../components/common/BusyOverlay";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import type { CertificateApplication } from "../types/certificate.types";
import {
  downloadReportPdf,
  printReportPdf,
  getReportPdfBase64,
} from "../utils/reportPdf";
import EmailReportModal from "../components/common/EmailReportModal";
import "../styles/pages/Reports.css";

const getLast30Days = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split("T")[0];
};

const getToday = () => new Date().toISOString().split("T")[0];

const MONTH_ABBREVIATIONS = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May.",
  "Jun.",
  "Jul.",
  "Aug.",
  "Sep.",
  "Oct.",
  "Nov.",
  "Dec.",
];

const formatReportDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const month = MONTH_ABBREVIATIONS[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

const toDate = (value?: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ApplicationsSummaryReport: React.FC = () => {
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [filters, setFilters] = useState({
    startDate: getLast30Days(),
    endDate: getToday(),
    category: "",
    status: "",
    county: "",
    search: "",
  });

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Applications Summary Report",
      "Track application volume and status trends",
    );
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await certificateService.getCertificateApplications();
        setApplications(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err?.message || "Failed to load applications report");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [setPageTitle]);

  const statusOptions = useMemo(
    () =>
      [
        ...new Set(
          applications
            .map((item) => item.statusName || item.status?.name)
            .filter(Boolean) as string[],
        ),
      ].sort(),
    [applications],
  );

  const countyOptions = useMemo(
    () =>
      [
        ...new Set(
          applications
            .map((item) => item.countyName || item.county?.name)
            .filter(Boolean) as string[],
        ),
      ].sort(),
    [applications],
  );

  const filteredRows = useMemo(() => {
    return applications.filter((item) => {
      const created = toDate(item.createdAt);
      const createdISO = created ? created.toISOString().split("T")[0] : "";

      const matchesDate =
        (!filters.startDate ||
          (createdISO && createdISO >= filters.startDate)) &&
        (!filters.endDate || (createdISO && createdISO <= filters.endDate));

      const matchesCategory =
        !filters.category ||
        (item.category || "").toLowerCase() === filters.category.toLowerCase();

      const resolvedStatus = (
        item.statusName ||
        item.status?.name ||
        ""
      ).toLowerCase();
      const matchesStatus =
        !filters.status || resolvedStatus === filters.status.toLowerCase();

      const resolvedCounty = (
        item.countyName ||
        item.county?.name ||
        ""
      ).toLowerCase();
      const matchesCounty =
        !filters.county || resolvedCounty === filters.county.toLowerCase();

      const searchable =
        `${item.formNo || ""} ${item.createdByName || ""} ${item.email || ""}`.toLowerCase();
      const matchesSearch =
        !filters.search || searchable.includes(filters.search.toLowerCase());

      return (
        matchesDate &&
        matchesCategory &&
        matchesStatus &&
        matchesCounty &&
        matchesSearch
      );
    });
  }, [applications, filters]);

  const metrics = useMemo(() => {
    const total = filteredRows.length;
    const marriageCount = filteredRows.filter(
      (item) => (item.category || "").toLowerCase() === "marriage",
    ).length;
    const divorceCount = filteredRows.filter(
      (item) => (item.category || "").toLowerCase() === "divorce",
    ).length;
    const approvedCount = filteredRows.filter((item) => {
      const status = (item.statusName || item.status?.name || "").toLowerCase();
      return (
        status.includes("approved") ||
        status.includes("final") ||
        status.includes("complete")
      );
    }).length;

    return { total, marriageCount, divorceCount, approvedCount };
  }, [filteredRows]);

  const [showEmailModal, setShowEmailModal] = useState(false);

  const buildPdfConfig = () => {
    const rows = filteredRows.map((item) => [
      item.formNo || "-",
      item.category || "-",
      item.statusName || item.status?.name || "-",
      item.countyName || item.county?.name || "-",
      item.email || "-",
      formatReportDate(item.createdAt),
    ]);

    return {
      title: "Applications Summary Report",
      filters: [
        { label: "Start Date", value: filters.startDate },
        { label: "End Date", value: filters.endDate },
        { label: "Category", value: filters.category || "All" },
        { label: "Status", value: filters.status || "All" },
        { label: "County", value: filters.county || "All" },
        { label: "Search", value: filters.search || "-" },
      ],
      columns: [
        "Form No.",
        "Category",
        "Status",
        "County",
        "Applicant Email",
        "Date Applied",
      ],
      rows,
    };
  };

  const handleDownloadPdf = () => {
    downloadReportPdf("applications-summary-report.pdf", buildPdfConfig());
  };

  const handlePrintPdf = () => {
    printReportPdf(buildPdfConfig());
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="reports-page">
          <BusyOverlay
            isVisible={loading}
            message="Loading applications summary..."
          />

          {error ? <div className="alert alert-error">{error}</div> : null}

          <div className="reports-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handlePrintPdf}
              disabled={filteredRows.length === 0}
            >
              Print PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleDownloadPdf}
              disabled={filteredRows.length === 0}
            >
              Download PDF
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setShowEmailModal(true)}
              disabled={filteredRows.length === 0}
            >
              ✉️ Email
            </button>
          </div>

          <EmailReportModal
            open={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            getAttachmentBase64={() => getReportPdfBase64(buildPdfConfig())}
            fileName="applications-summary-report.pdf"
            defaultSubject="Applications Summary Report"
            defaultMessage="Please find the attached Applications Summary Report."
          />

          <div className="reports-filters">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Category</label>
              <select
                className="filter-select"
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <option value="">All Categories</option>
                <option value="Marriage">Marriage</option>
                <option value="Divorce">Divorce</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                className="filter-select"
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">All Statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">County</label>
              <select
                className="filter-select"
                value={filters.county}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, county: e.target.value }))
                }
              >
                <option value="">All Counties</option>
                {countyOptions.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Form no, creator, email"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="reports-kpis">
            <div className="kpi-card">
              <span className="kpi-label">Total Applications</span>
              <span className="kpi-value">{metrics.total}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Marriage</span>
              <span className="kpi-value">{metrics.marriageCount}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Divorce</span>
              <span className="kpi-value">{metrics.divorceCount}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Approved/Completed</span>
              <span className="kpi-value">{metrics.approvedCount}</span>
            </div>
          </div>

          <div className="reports-table-container">
            <div className="table-wrapper">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Form No.</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>County</th>
                    <th>Applicant Email</th>
                    <th>Date Applied</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((item) => (
                    <tr key={item.id || `${item.formNo}-${item.createdAt}`}>
                      <td>{item.formNo || "-"}</td>
                      <td>{item.category || "-"}</td>
                      <td>{item.statusName || item.status?.name || "-"}</td>
                      <td>{item.countyName || item.county?.name || "-"}</td>
                      <td>{item.email || "-"}</td>
                      <td>{formatReportDate(item.createdAt)}</td>
                      <td>
                        <button
                          onClick={() =>
                            navigate(`/reports/application-dossier/${item.id}`)
                          }
                          style={{
                            padding: "5px 10px",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          📋 Dossier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default ApplicationsSummaryReport;
